import { FetchError } from "@medusajs/js-sdk"
import { HttpTypes } from "@medusajs/types"
import {
  QueryKey,
  useMutation,
  UseMutationOptions,
  useQuery,
  UseQueryOptions,
} from "@tanstack/react-query"
import { backendUrl, sdk } from "../../lib/client"
import { queryClient } from "../../lib/query-client"
import { queryKeysFactory } from "../../lib/query-key-factory"
import { normalizeAdminLanguageCode } from "../../i18n/supported-admin-languages"
import { i18n } from "../../components/utilities/i18n/i18n"
import { inventoryItemsQueryKeys } from "./inventory.tsx"

const PRODUCTS_QUERY_KEY = "products" as const
export const productsQueryKeys = queryKeysFactory(PRODUCTS_QUERY_KEY)

const VARIANTS_QUERY_KEY = "product_variants" as const
export const variantsQueryKeys = queryKeysFactory(VARIANTS_QUERY_KEY)

const OPTIONS_QUERY_KEY = "product_options" as const
export const optionsQueryKeys = queryKeysFactory(OPTIONS_QUERY_KEY)

const getCatalogLocale = () => normalizeAdminLanguageCode(i18n.language)

const getCatalogLocaleHeaders = () => ({
  "x-catalog-locale": getCatalogLocale(),
})

type CatalogTranslationLocaleCardPayload = {
  locale: "en" | "vi"
  mode: "auto" | "manual"
  manual_content?: {
    title?: string | null
    subtitle?: string | null
    description?: string | null
  }
}

type ProductCatalogTranslationPayload = {
  catalog_translation?: {
    locale_cards: CatalogTranslationLocaleCardPayload[]
  }
}

type RetranslateProductLocalePayload = {
  trigger: "retry_failed" | "force_retranslate"
  request_id: string
  expected_source_snapshot_hash?: string
}

export const useCreateProductOption = (
  productId: string,
  options?: UseMutationOptions<any, FetchError, any>
) => {
  return useMutation({
    mutationFn: (payload: HttpTypes.AdminCreateProductOption) =>
      sdk.admin.product.createOption(productId, payload),
    onSuccess: (data: any, variables: any, context: any) => {
      queryClient.invalidateQueries({ queryKey: optionsQueryKeys.lists() })
      queryClient.invalidateQueries({
        queryKey: productsQueryKeys.detail(productId),
      })
      options?.onSuccess?.(data, variables, context)
    },
    ...options,
  })
}

export const useUpdateProductOption = (
  productId: string,
  optionId: string,
  options?: UseMutationOptions<any, FetchError, any>
) => {
  return useMutation({
    mutationFn: (payload: HttpTypes.AdminUpdateProductOption) =>
      sdk.admin.product.updateOption(productId, optionId, payload),
    onSuccess: (data: any, variables: any, context: any) => {
      queryClient.invalidateQueries({ queryKey: optionsQueryKeys.lists() })
      queryClient.invalidateQueries({
        queryKey: optionsQueryKeys.detail(optionId),
      })
      queryClient.invalidateQueries({
        queryKey: productsQueryKeys.detail(productId),
      })

      options?.onSuccess?.(data, variables, context)
    },
    ...options,
  })
}

export const useDeleteProductOption = (
  productId: string,
  optionId: string,
  options?: UseMutationOptions<any, FetchError, void>
) => {
  return useMutation({
    mutationFn: () => sdk.admin.product.deleteOption(productId, optionId),
    onSuccess: (data: any, variables: any, context: any) => {
      queryClient.invalidateQueries({ queryKey: optionsQueryKeys.lists() })
      queryClient.invalidateQueries({
        queryKey: optionsQueryKeys.detail(optionId),
      })
      queryClient.invalidateQueries({
        queryKey: productsQueryKeys.detail(productId),
      })

      options?.onSuccess?.(data, variables, context)
    },
    ...options,
  })
}

export const useProductVariant = (
  productId: string,
  variantId: string,
  query?: HttpTypes.AdminProductVariantParams,
  options?: Omit<
    UseQueryOptions<
      HttpTypes.AdminProductVariantResponse,
      FetchError,
      HttpTypes.AdminProductVariantResponse,
      QueryKey
    >,
    "queryFn" | "queryKey"
  >
) => {
  const { data, ...rest } = useQuery({
    queryFn: () =>
      sdk.admin.product.retrieveVariant(productId, variantId, query),
    queryKey: variantsQueryKeys.detail(variantId, query),
    ...options,
  })

  return { ...data, ...rest }
}

export const useProductVariants = (
  productId: string,
  query?: HttpTypes.AdminProductVariantParams,
  options?: Omit<
    UseQueryOptions<
      HttpTypes.AdminProductVariantListResponse,
      FetchError,
      HttpTypes.AdminProductVariantListResponse,
      QueryKey
    >,
    "queryFn" | "queryKey"
  >
) => {
  const { data, ...rest } = useQuery({
    queryFn: () => sdk.admin.product.listVariants(productId, query),
    queryKey: variantsQueryKeys.list({ productId, ...query }),
    ...options,
  })

  return { ...data, ...rest }
}

export const useCreateProductVariant = (
  productId: string,
  options?: UseMutationOptions<any, FetchError, any>
) => {
  return useMutation({
    mutationFn: (payload: HttpTypes.AdminCreateProductVariant) =>
      sdk.admin.product.createVariant(productId, payload),
    onSuccess: (data: any, variables: any, context: any) => {
      queryClient.invalidateQueries({ queryKey: variantsQueryKeys.lists() })
      queryClient.invalidateQueries({
        queryKey: productsQueryKeys.detail(productId),
      })
      options?.onSuccess?.(data, variables, context)
    },
    ...options,
  })
}

export const useUpdateProductVariant = (
  productId: string,
  variantId: string,
  options?: UseMutationOptions<any, FetchError, any>
) => {
  return useMutation({
    mutationFn: (payload: HttpTypes.AdminUpdateProductVariant) =>
      sdk.admin.product.updateVariant(productId, variantId, payload),
    onSuccess: (data: any, variables: any, context: any) => {
      queryClient.invalidateQueries({ queryKey: variantsQueryKeys.lists() })
      queryClient.invalidateQueries({
        queryKey: variantsQueryKeys.detail(variantId),
      })
      queryClient.invalidateQueries({
        queryKey: productsQueryKeys.detail(productId),
      })

      options?.onSuccess?.(data, variables, context)
    },
    ...options,
  })
}

export const useUpdateProductVariantsBatch = (
  productId: string,
  options?: UseMutationOptions<any, FetchError, any>
) => {
  return useMutation({
    mutationFn: (
      payload: HttpTypes.AdminBatchProductVariantRequest["update"]
    ) =>
      sdk.admin.product.batchVariants(productId, {
        update: payload,
      }),
    onSuccess: (data: any, variables: any, context: any) => {
      queryClient.invalidateQueries({ queryKey: variantsQueryKeys.lists() })
      queryClient.invalidateQueries({ queryKey: variantsQueryKeys.details() })
      queryClient.invalidateQueries({
        queryKey: productsQueryKeys.detail(productId),
      })

      options?.onSuccess?.(data, variables, context)
    },
    ...options,
  })
}

export const useProductVariantsInventoryItemsBatch = (
  productId: string,
  options?: UseMutationOptions<
    HttpTypes.AdminBatchProductVariantInventoryItemResponse,
    FetchError,
    HttpTypes.AdminBatchProductVariantInventoryItemRequest
  >
) => {
  return useMutation({
    mutationFn: (payload) =>
      sdk.admin.product.batchVariantInventoryItems(productId, payload),
    onSuccess: (data: any, variables: any, context: any) => {
      queryClient.invalidateQueries({ queryKey: variantsQueryKeys.lists() })
      queryClient.invalidateQueries({ queryKey: variantsQueryKeys.details() })
      queryClient.invalidateQueries({
        queryKey: productsQueryKeys.detail(productId),
      })

      options?.onSuccess?.(data, variables, context)
    },
    ...options,
  })
}

export const useDeleteVariant = (
  productId: string,
  variantId: string,
  options?: UseMutationOptions<any, FetchError, void>
) => {
  return useMutation({
    mutationFn: () => sdk.admin.product.deleteVariant(productId, variantId),
    onSuccess: (data: any, variables: any, context: any) => {
      queryClient.invalidateQueries({ queryKey: variantsQueryKeys.lists() })
      queryClient.invalidateQueries({
        queryKey: variantsQueryKeys.detail(variantId),
      })
      queryClient.invalidateQueries({
        queryKey: productsQueryKeys.detail(productId),
      })

      options?.onSuccess?.(data, variables, context)
    },
    ...options,
  })
}

export const useDeleteVariantLazy = (
  productId: string,
  options?: UseMutationOptions<
    HttpTypes.AdminProductVariantDeleteResponse,
    FetchError,
    { variantId: string }
  >
) => {
  return useMutation({
    mutationFn: ({ variantId }) =>
      sdk.admin.product.deleteVariant(productId, variantId),
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: variantsQueryKeys.lists() })
      queryClient.invalidateQueries({
        queryKey: variantsQueryKeys.detail(variables.variantId),
      })
      queryClient.invalidateQueries({
        queryKey: productsQueryKeys.detail(productId),
      })

      options?.onSuccess?.(data, variables, context)
    },
    ...options,
  })
}

export const useProduct = (
  id: string,
  query?: Record<string, any>,
  options?: Omit<
    UseQueryOptions<
      HttpTypes.AdminProductResponse,
      FetchError,
      HttpTypes.AdminProductResponse,
      QueryKey
    >,
    "queryFn" | "queryKey"
  >
) => {
  const locale = getCatalogLocale()

  const { data, ...rest } = useQuery({
    queryFn: () =>
      sdk.client.fetch<HttpTypes.AdminProductResponse>(`/admin/products/${id}`, {
        method: "GET",
        query,
        headers: getCatalogLocaleHeaders(),
      }),
    queryKey: productsQueryKeys.detail(id, {
      ...(query ?? {}),
      locale,
    }),
    ...options,
  })

  return { ...data, ...rest }
}

export const useProducts = (
  query?: HttpTypes.AdminProductListParams,
  options?: Omit<
    UseQueryOptions<
      HttpTypes.AdminProductListResponse,
      FetchError,
      HttpTypes.AdminProductListResponse,
      QueryKey
    >,
    "queryFn" | "queryKey"
  >
) => {
  const locale = getCatalogLocale()

  const { data, ...rest } = useQuery({
    queryFn: () =>
      sdk.client.fetch<HttpTypes.AdminProductListResponse>("/admin/products", {
        method: "GET",
        query,
        headers: getCatalogLocaleHeaders(),
      }),
    queryKey: productsQueryKeys.list({
      ...(query ?? {}),
      locale,
    }),
    ...options,
  })

  return { ...data, ...rest }
}

export const useCreateProduct = (
  options?: UseMutationOptions<
    HttpTypes.AdminProductResponse,
    FetchError,
    HttpTypes.AdminCreateProduct &
      ProductCatalogTranslationPayload & { merchant_id?: string }
  >
) => {
  return useMutation({
    mutationFn: (payload) => {
      const { merchant_id, ...rest } = payload ?? {}
      const query = merchant_id
        ? `?merchant_id=${encodeURIComponent(merchant_id)}`
        : ""
      return sdk.client.fetch<HttpTypes.AdminProductResponse>(
        `/admin/products${query}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...getCatalogLocaleHeaders(),
          },
          body: rest,
        }
      )
    },
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: productsQueryKeys.lists() })
      // if `manage_inventory` is true on created variants that will create inventory items automatically
      queryClient.invalidateQueries({
        queryKey: inventoryItemsQueryKeys.lists(),
      })
      options?.onSuccess?.(data, variables, context)
    },
    ...options,
  })
}

export const useUpdateProduct = (
  id: string,
  options?: UseMutationOptions<
    HttpTypes.AdminProductResponse,
    FetchError,
    HttpTypes.AdminUpdateProduct & ProductCatalogTranslationPayload
  >
) => {
  return useMutation({
    mutationFn: (payload) => sdk.admin.product.update(id, payload),
    onSuccess: async (data, variables, context) => {
      await queryClient.invalidateQueries({
        queryKey: productsQueryKeys.lists(),
      })
      await queryClient.invalidateQueries({
        queryKey: productsQueryKeys.detail(id),
      })

      options?.onSuccess?.(data, variables, context)
    },
    ...options,
  })
}

export const useRetranslateProductLocale = (
  id: string,
  options?: UseMutationOptions<
    {
      translation_action: {
        accepted: true
        idempotent_replay: boolean
        locale: string
        status: "pending"
        source_snapshot_hash: string
        queued_at: string
        request_id: string
        trigger: "retry_failed" | "force_retranslate"
      }
    },
    FetchError,
    { locale: string; payload: RetranslateProductLocalePayload }
  >
) => {
  return useMutation({
    mutationFn: ({ locale, payload }) =>
      sdk.client.fetch(`/admin/products/${id}/translations/${locale}/retranslate`, {
        method: "POST",
        body: payload,
      }),
    onSuccess: async (data, variables, context) => {
      await queryClient.invalidateQueries({
        queryKey: productsQueryKeys.lists(),
      })
      await queryClient.invalidateQueries({
        queryKey: productsQueryKeys.detail(id),
      })

      options?.onSuccess?.(data, variables, context)
    },
    ...options,
  })
}

export const useDeleteProduct = (
  id: string,
  options?: UseMutationOptions<
    HttpTypes.AdminProductDeleteResponse,
    FetchError,
    void
  >
) => {
  return useMutation({
    mutationFn: () => sdk.admin.product.delete(id),
    onSuccess: (data: any, variables: any, context: any) => {
      queryClient.invalidateQueries({ queryKey: productsQueryKeys.lists() })
      queryClient.invalidateQueries({ queryKey: productsQueryKeys.detail(id) })

      options?.onSuccess?.(data, variables, context)
    },
    ...options,
  })
}

export const useExportProducts = (
  query?: HttpTypes.AdminProductListParams,
  options?: UseMutationOptions<
    HttpTypes.AdminExportProductResponse,
    FetchError,
    HttpTypes.AdminExportProductRequest
  >
) => {
  return useMutation({
    mutationFn: (payload) => sdk.admin.product.export(payload, query),
    onSuccess: (data, variables, context) => {
      options?.onSuccess?.(data, variables, context)
    },
    ...options,
  })
}

export type AdminImportProductsParams = HttpTypes.AdminImportProductRequest & {
  merchant_id?: string
}

export const useImportProducts = (
  options?: UseMutationOptions<
    HttpTypes.AdminImportProductResponse,
    FetchError,
    AdminImportProductsParams
  >
) => {
  return useMutation({
    mutationFn: async (payload) => {
      const { merchant_id, file } = payload ?? {}
      const formData = new FormData()
      formData.append("file", file as File)

      const query = merchant_id
        ? `?merchant_id=${encodeURIComponent(merchant_id)}`
        : ""
      const importPath = `/admin/products/import${query}`
      const importUrl =
        typeof backendUrl === "string" && /^https?:\/\//i.test(backendUrl)
          ? new URL(importPath, backendUrl).toString()
          : importPath

      const resp = await fetch(importUrl, {
        method: "POST",
        credentials: "include",
        headers: { accept: "application/json" },
        body: formData,
      })

      const json = await resp.json().catch(() => null)
      if (!resp.ok) {
        const message =
          typeof json?.message === "string" && json.message.trim().length
            ? json.message
            : `Request failed (${resp.status})`
        const e: any = new Error(message)
        e.status = resp.status
        e.data = json
        throw e
      }
      return json as HttpTypes.AdminImportProductResponse
    },
    onSuccess: (data, variables, context) => {
      options?.onSuccess?.(data, variables, context)
    },
    ...options,
  })
}

export const useConfirmImportProducts = (
  options?: UseMutationOptions<
    {},
    FetchError,
    { transaction_id: string; merchant_id?: string }
  >
) => {
  return useMutation({
    mutationFn: ({ transaction_id, merchant_id }) => {
      const query = merchant_id
        ? `?merchant_id=${encodeURIComponent(merchant_id)}`
        : ""
      const tx = encodeURIComponent(transaction_id)
      return sdk.client.fetch(`/admin/products/imports/${tx}/confirm${query}`, {
        method: "POST",
      })
    },
    onSuccess: (data, variables, context) => {
      options?.onSuccess?.(data, variables, context)
    },
    ...options,
  })
}

export type AdminImportProductsStatusResponse = {
  transaction_id: string
  status: "awaiting_confirmation" | "processing" | "linking" | "done" | "failed"
  steps: {
    workflow: "pending" | "processing" | "done" | "failed" | "unknown"
    ownership: "pending" | "processing" | "done" | "failed" | "unknown"
  }
  created_at: number
  updated_at: number
  expires_at: number
  last_error?: { message: string }
}

export const useImportProductsStatus = (
  {
    transaction_id,
    merchant_id,
  }: {
    transaction_id?: string
    merchant_id?: string
  },
  options?: Omit<
    UseQueryOptions<
      AdminImportProductsStatusResponse,
      FetchError,
      AdminImportProductsStatusResponse,
      QueryKey
    >,
    "queryKey" | "queryFn"
  >
) => {
  const tx = transaction_id ? encodeURIComponent(transaction_id) : ""
  const query = merchant_id
    ? `?merchant_id=${encodeURIComponent(merchant_id)}`
    : ""

  return useQuery({
    queryKey: [
      "product-import-status",
      transaction_id ?? null,
      merchant_id ?? null,
    ],
    queryFn: () =>
      sdk.client.fetch<AdminImportProductsStatusResponse>(
        `/admin/products/imports/${tx}${query}`,
        {
          method: "GET",
        }
      ),
    enabled: Boolean(tx && merchant_id) && (options?.enabled ?? true),
    ...options,
  })
}
